import { makeObservable, computed } from 'mobx';
import { uniq } from 'lodash';
import { Disposer } from '../../../custom_shared/misc';
import { InputIds } from '../../misc';
import { ContractComparisonPanel, SectionPair } from '../../types';
import type InputBaseModel from '../inputs/InputBaseModel';
import type { AggregatedChanges } from '../../change_trackers/InputGroupChangeTracker';

type InputLookup = (inputId: string) => InputBaseModel | undefined;

export type ComparisonInputGroup = {
    id: string;
    panelId: string;
    inputIds: string[];
    changes: AggregatedChanges;
    getNewInputById: InputLookup;
    getPreviousInputById: InputLookup;
    isHidden?: boolean;
    isDisabled?: boolean;
    pair?: SectionPair;
};

export default abstract class ComparisonNodeBaseModel extends Disposer {
    readonly hiddenInputIds: string[] = [
        // Create program is not part of SICS response
        InputIds.GeneralInformation.CreateProgram,
        // Business type is the same and we can't allow to change it in comparison view it will cause a lot of layout changes
        InputIds.GeneralInformation.BusinessType,
        // Brokerage type is not part of SICS response
        InputIds.Reinstatements.BrokerageType
    ];

    constructor(
        readonly nodeId: string,
        readonly nodeName: string,
        readonly predefinedPanels?: ContractComparisonPanel[]
    ) {
        super();

        makeObservable(this, {
            panels: computed
        });
    }

    dispose() {
        this.disposeReactions();
    }

    abstract get inputIds(): string[];

    abstract get groups(): ComparisonInputGroup[];

    protected getInputIds(newNodeInputIds: string[] = [], previousNodeInputIds: string[] = []): string[] {
        return uniq([...newNodeInputIds, ...previousNodeInputIds]).filter(
            inputId =>
                !this.hiddenInputIds.includes(inputId) &&
                // Extra limits and deductions are skipped for now, UI must contain only unique codes, which will be implemented in separate task
                !inputId.startsWith(InputIds.Limits.LimitType) &&
                !inputId.startsWith(InputIds.Limits.LimitValue) &&
                !inputId.startsWith(InputIds.Deductions.DeductionType) &&
                !inputId.startsWith(InputIds.Deductions.DeductionValue)
        );
    }

    get panels() {
        // Use predefined panels if provided
        if (this.predefinedPanels) {
            return this.predefinedPanels.map(panel => ({
                ...panel,
                inputIds: panel.inputIds.filter(inputId => this.inputIds.includes(inputId))
            }));
        }

        // Otherwise, create a default panel with all input ids
        return [
            {
                id: this.nodeId,
                name: this.nodeName,
                inputIds: this.inputIds
            }
        ];
    }
}
